import type {Operation} from '@apollo/client';

import {getInteropAccountId} from '../../../interopAccountId';
import {ACCOUNT_HEADER} from '../../../addContextHeaders';
import type {PathBuilderParams, RestLinkOperationContext} from './types';

export type InteropFields = {
  query: Record<string, boolean>;
  mutation: Record<string, boolean>;
  subscription: Record<string, boolean>;
};

type HeaderLike = {
  get?: (name: string) => string | null;
  set?: (name: string, value: string) => void;
} & Record<string, string | unknown>;

function getHeaderValue(headers: HeaderLike | undefined, name: string) {
  if (!headers) {
    return undefined;
  }

  if (typeof headers.get === 'function') {
    return headers.get(name) ?? undefined;
  }

  const value = headers[name];
  return typeof value === 'string' ? value : undefined;
}

function setHeaderValue(
  headers: HeaderLike | undefined,
  name: string,
  value: string,
) {
  if (!headers) {
    return;
  }

  if (typeof headers.set === 'function') {
    headers.set(name, value);
    return;
  }

  headers[name] = value;
}

// Rewrites variables, headers, and per-field input for interop operations
// running in an org context. Called from buildRestPath once per top-level
// field.
export default function rewriteInteropParams(
  operation: Operation,
  params: PathBuilderParams,
  interopFields: InteropFields | undefined,
  observabilityFns?: RestLinkOperationContext['observabilityFns'],
) {
  if (!interopFields) {
    return;
  }

  const fieldName = params.fieldName;
  const opType = params.context.mainDefinition.operation;

  if (!interopFields[opType]?.[fieldName]) {
    return;
  }

  const interopAccountId = getInteropAccountId();

  if (!interopAccountId) {
    return;
  }

  if (params.args?.input?.root_compartment_id) {
    const v1Context = operation.variables.v1Context as
      | {id?: string}
      | undefined;

    if (v1Context?.id && v1Context.id !== interopAccountId) {
      observabilityFns?.errors?.warning(
        'v1Context.id does not match interopAccountId when root_compartment_id is present [v1]',
        {
          project: 'sail_core',
          extras: {
            v1ContextId: v1Context.id,
            interopAccountId,
            fieldName: params.fieldName,
          },
        },
      );
    } else if (Math.random() < 0.0001) {
      observabilityFns?.analytics?.track('interop_root_compartment_id_match', {
        project: 'sail_core',
        v1_context_id: v1Context?.id,
        interop_account_id: interopAccountId,
        field_name: params.fieldName,
      });
    }
  }

  const context = operation.getContext();
  // apollo-link-rest snapshots requestContext.headers before calling pathBuilder,
  // so header rewrites must update that object directly to affect the fetch.
  const requestContext = params.context as PathBuilderParams['context'] & {
    headers?: HeaderLike;
  };
  let orgId: string | undefined = context.__interopOrgId;

  // Only the first interop field in an operation needs to rewrite v1Context.id
  // and Stripe-Account. Later fields reuse the cached org id below.
  if (!orgId) {
    const v1Context = operation.variables.v1Context as
      | {id?: string}
      | undefined;

    if (!v1Context?.id?.startsWith('org_')) {
      return;
    }

    orgId = v1Context.id;

    operation.variables = {
      ...operation.variables,
      v1Context: {...v1Context, id: interopAccountId},
    };

    const shouldRewriteAccountHeader =
      getHeaderValue(requestContext.headers, ACCOUNT_HEADER) === orgId ||
      getHeaderValue(context.headers, ACCOUNT_HEADER) === orgId;

    if (shouldRewriteAccountHeader) {
      setHeaderValue(requestContext.headers, ACCOUNT_HEADER, interopAccountId);
      operation.setContext({
        headers: {
          ...context.headers,
          [ACCOUNT_HEADER]: interopAccountId,
        },
        __interopOrgId: orgId,
      });
    } else {
      operation.setContext({
        __interopOrgId: orgId,
      });
    }
  }

  // Every interop field still needs the original org id in input
  // as root_compartment_id.
  if (params.args && orgId) {
    params.args = {
      ...params.args,
      input: {
        ...(params.args.input || {}),
        root_compartment_id: orgId,
      },
    };
  }
}
